<template>
  <div class="content">
    <!-- User -->
    <div class="person">
      <!-- Single user -->
      <div
        v-if="people?.length === 1"
        style="display: flex; align-items: center">
        <div class="person__avatar">
          <SmAvatar :name="people[0].name || people[0].email || '??'" />
        </div>
        <div class="person__information">
          <div class="person__name">
            {{ people[0].name }}
          </div>
          <div class="person__email">
            {{ people[0].email }}
          </div>
        </div>
      </div>

      <!-- Multiple users -->
      <div v-else style="display: flex; align-items: center">
        <div v-if="people" class="person__avatar">
          <SmAvatar :name="people?.length.toString()" />
        </div>

        <div class="person__information">
          <div class="person__name">
            {{ people?.length }}
            {{ i18n.t('person', { count: people?.length }) }}
          </div>
          <div class="person__email">
            {{ i18n.t('multipleLicenceChangesWarning') }}
          </div>
        </div>
      </div>
    </div>

    <!-- Message -->
    <SmDialogMessage
      v-if="result"
      :type="result.status"
      :message="result.message">
      {{ result.message }}
      <br />
      <!-- One time password -->
      <span
        v-if="
          result.response &&
          result.response?.application_specific &&
          result.response?.application_specific.default_password
        ">
        {{ i18n.t('initialPassword') }}:
        <b>
          {{ result.response?.application_specific.default_password }}</b
        ></span
      >
    </SmDialogMessage>
    <!-- License -->
    <div
      style="
        max-height: 350px;
        overflow: scroll;
        margin-top: 1.3rem;
        overflow-x: hidden;
      ">
      <!-- License Model Edit Table -->
      <LicenseModelEditTable
        v-if="people && application"
        ref="softwareLicenseEditTable"
        :license="license"
        filter-flatrate
        :software-licenses="softwareLicenses"
        :application="application"
        :user="people"
        :sub-account-id="subAccount?.id"
        @licenses-changed="emit('editedLicense')"
        @user-added="emit('userAdded')" />
    </div>

    <!-- Button -->
    <div class="button">
      <SmButton
        :disabled="!people"
        size="large"
        :loading="state === 'loading'"
        @click="handleButtonClick">
        <span v-if="state === 'selection'">{{ i18n.t('save') }}</span>
        <span v-else-if="state === 'loading'">{{
          i18n.t('licenseAddingInProcess')
        }}</span>
        <span v-else-if="state === 'done'">{{ i18n.t('close') }}</span>
      </SmButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { ComponentExposed } from 'vue-component-type-helpers'
  import { useI18n } from 'vue-i18n'

  import {
    Account,
    LicenceOut,
    SoftwareLicense,
    SoftwareOut,
    SubAccount_Output,
  } from '@/client'

  import LicenseModelEditTable from '@/components/license/LicenseModelEditTable.vue'
  import SmAvatar from '@/components/sm/SmAvatar.vue'

  const i18n = useI18n()

  const softwareLicenseEditTable =
    ref<ComponentExposed<typeof LicenseModelEditTable>>()

  const emit = defineEmits<{
    (e: 'userAdded'): void
    (e: 'close'): void
    (e: 'saved'): void
    (e: 'editedLicense'): void
  }>()

  defineProps({
    softwareLicenses: {
      type: Object as PropType<SoftwareLicense[]>,
      required: true,
    },
    application: {
      type: Object as PropType<SoftwareOut>,
      required: true,
    },
    people: {
      type: Object as PropType<Account[] | null>,
      required: true,
    },
    license: {
      type: Object as PropType<LicenceOut>,
      required: false,
      default: null,
    },
    subAccount: {
      type: Object as PropType<SubAccount_Output | null>,
      required: false,
      default: null,
    },
    hideHeader: {
      type: Boolean,
      required: false,
      default: false,
    },
  })

  const state = computed(() => {
    return softwareLicenseEditTable.value?.state
  })

  const result = computed(() => {
    return softwareLicenseEditTable.value?.result
  })

  // Main Button actions
  async function handleButtonClick() {
    if (state.value === 'done') {
      emit('close')
    } else {
      await softwareLicenseEditTable.value?.execute()
      emit('saved')
    }
  }
</script>

<style lang="scss" scoped>
  .person {
    display: flex;
    align-items: center;
    margin-bottom: 1rem;
    max-width: 100%;

    &__name {
      font-weight: 500;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    &__avatar {
      margin-right: 1rem;
      border-radius: var(--border-radius-base);
      background-color: var(--sm-white);
    }
  }

  .button {
    width: 70%;
    margin: 30px auto 0 auto;
  }

  .licenses {
    .license {
      display: flex;
      width: 100%;
      flex-direction: row;
      justify-content: space-between;
      gap: 1rem;
    }

    width: 100%;
    margin-top: 0.5rem;
  }

  .alert {
    $message-padding: 0.5rem;
    margin: 1.5rem 0 0 0;
    padding: $message-padding;
    width: calc(100% - #{$message-padding * 2});
    border-radius: var(--border-radius-base);
    display: flex;
    align-items: center;

    &-success {
      border: 1px solid var(--sm-primary);
    }

    &-error {
      border: 1px solid var(--sm-magenta);
    }

    .message {
      margin-left: 0.5rem;
    }
  }
</style>
